/**
 * Phase G+ Phase 9 (19.06.2026) — Offboarding-Wizard Modal.
 * --------------------------------------------------------
 * 3-Step guided workflow when an employee leaves:
 *   1. Stichtag bestätigen + Notiz
 *   2. Resturlaub-Auszahlung + offene Akonti prüfen
 *   3. Bestätigen → Status auf 'abgemeldet' + Settlement-Doc + Akonti closen
 *
 * The modal pulls the preview lazily, shows the settlement numbers from
 * the same engine used by Payroll-Vorschau (single source of truth).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X as XIcon, ArrowRight, ArrowLeft, Check, AlertTriangle, Sparkles } from "lucide-react";
import { fmtCur } from "@/lib/format";

export default function OffboardingWizard({ empId, empName, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [exitDate, setExitDate] = useState(today);
  const [payOutVacation, setPayOutVacation] = useState(true);
  const [forgiveAdvances, setForgiveAdvances] = useState(false);
  const [note, setNote] = useState("");
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    api.get(`/employees/${empId}/offboard/preview`)
      .then(r => setPreview(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen"));
  }, [empId]);

  const commit = async () => {
    if (committing) return;
    setCommitting(true);
    try {
      const r = await api.post(`/employees/${empId}/offboard/commit`, {
        exit_date: exitDate,
        pay_out_remaining_vacation: payOutVacation,
        forgive_active_advances: forgiveAdvances,
        note: note.trim() || null,
      });
      toast.success(`Mitarbeiter abgemeldet · Netto-Auszahlung ${fmtCur(r.data.offboarding.settlement.net_settlement_eur)}`);
      onDone?.(r.data.offboarding);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Offboarding fehlgeschlagen");
    } finally {
      setCommitting(false);
    }
  };

  if (!preview) {
    return (
      <Modal onClose={onClose}>
        <div className="p-6 text-center text-zinc-400 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Offboarding-Vorschau…
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} testid="offboarding-wizard">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div>
          <div className="font-display text-base font-semibold text-zinc-100">Offboarding · {empName}</div>
          <div className="text-[11px] text-zinc-500">Schritt {step} von 3</div>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-4 text-sm">
        {/* STEP 1 — Stichtag */}
        {step === 1 && (
          <div className="space-y-3" data-testid="offb-step-1">
            <p className="text-zinc-400">
              Setze das letzte Dienst-Datum (Stichtag). Resturlaub + offene Akonti werden
              im nächsten Schritt automatisch berechnet.
            </p>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Stichtag (letztes Dienst-Datum)</label>
              <input type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)}
                     data-testid="offb-exit-date"
                     className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"/>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Notiz (optional, für Audit-Log)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        data-testid="offb-note"
                        placeholder="z.B. Eigenkündigung zum Monatsende"
                        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"/>
            </div>
          </div>
        )}

        {/* STEP 2 — Settlement-Vorschau */}
        {step === 2 && (
          <div className="space-y-3" data-testid="offb-step-2">
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Resturlaub" value={`${preview.remaining_vacation_days?.toFixed(1)} Tage`}/>
              <KPI label="Tagessatz" value={fmtCur(preview.daily_rate_eur)}/>
              <KPI label="Resturlaub-Wert" value={fmtCur(preview.vacation_payout_eur)} tone="emerald"/>
              <KPI label="Offene Akonti" value={fmtCur(preview.active_advances_open_eur)} tone={preview.active_advances_open_eur > 0 ? "amber" : "zinc"}/>
            </div>

            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={payOutVacation} onChange={(e) => setPayOutVacation(e.target.checked)}
                     data-testid="offb-payout-vacation"/>
              <span>Resturlaub auszahlen ({fmtCur(preview.vacation_payout_eur)})</span>
            </label>

            {preview.active_advances_open_eur > 0 && (
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={forgiveAdvances} onChange={(e) => setForgiveAdvances(e.target.checked)}
                       data-testid="offb-forgive-advances"/>
                <span>Offene Akonti erlassen ({fmtCur(preview.active_advances_open_eur)}) — sonst wird abgezogen</span>
              </label>
            )}

            <div className="rounded-md border border-zinc-700 bg-zinc-900 p-3 text-xs">
              <div className="text-zinc-400">Netto-Auszahlung (Endabrechnung):</div>
              <div className="mt-1 font-display text-2xl font-bold text-emerald-300">
                {fmtCur(
                  (payOutVacation ? preview.vacation_payout_eur : 0)
                  - (forgiveAdvances ? 0 : preview.active_advances_open_eur)
                )}
              </div>
            </div>

            {preview.daily_rate_eur === 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Kein Tageslohn berechenbar (kein Brutto-Lohn gepflegt). Resturlaub-Wert = 0.
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Bestätigung */}
        {step === 3 && (
          <div className="space-y-3" data-testid="offb-step-3">
            <p className="text-zinc-300">
              Beim Klick auf „Offboarding bestätigen" passiert folgendes:
            </p>
            <ul className="text-xs text-zinc-400 list-disc list-inside space-y-1">
              <li>Status wird auf <code className="font-mono text-rose-300">abgemeldet</code> gesetzt</li>
              <li>Exit-Datum: <b>{exitDate}</b></li>
              <li>
                {payOutVacation
                  ? <>Resturlaub-Auszahlung: <b>{fmtCur(preview.vacation_payout_eur)}</b></>
                  : "Resturlaub wird NICHT ausgezahlt"}
              </li>
              <li>
                {forgiveAdvances
                  ? "Offene Akonti werden als 'completed' geschlossen (erlassen)"
                  : "Offene Akonti werden als 'cancelled' geschlossen (abgezogen)"}
              </li>
              <li>Audit-Eintrag wird geschrieben</li>
            </ul>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Diese Aktion kann nicht rückgängig gemacht werden. Aber: alle Werte
              werden im Audit-Log + Offboardings-Doc permanent gesichert.
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between bg-zinc-950">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200">
          <ArrowLeft className="w-3.5 h-3.5" /> {step === 1 ? "Abbrechen" : "Zurück"}
        </button>
        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            data-testid="offb-next"
            className="inline-flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
            Weiter <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={commit}
            disabled={committing}
            data-testid="offb-commit"
            className="inline-flex items-center gap-1.5 text-xs bg-rose-600 hover:bg-rose-500 disabled:bg-zinc-700 text-white px-3 py-1.5 rounded">
            {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Offboarding bestätigen
          </button>
        )}
      </div>
    </Modal>
  );
}

function KPI({ label, value, tone }) {
  const toneCls = { emerald: "text-emerald-300", amber: "text-amber-300" }[tone] || "text-zinc-100";
  return (
    <div className="bg-zinc-900 rounded p-2">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-0.5 font-display font-bold text-base ${toneCls}`}>{value}</div>
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid={testid}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full"
           onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
